/**
 * Cognitoのグループ名から、サイドバーに出す日本語ラベルを組み立てる。
 *
 * スケジュール画面(FilterSidebar)とメモ画面(MemoFilterSidebar)で同じ処理を
 * 持っていたためここへ集約した。両者の差はメモ画面だけが「記者」を
 * 付ける点だけなので、オプションで切り替える。
 */

import { getBranchGroupLabel } from './branchGroupLabel';

/** 本社の部署グループ→ラベル。ここでの並び順がそのまま表示順になる */
const DEPARTMENT_GROUP_LABELS: Record<string, string> = {
  'group_society': '本社・社会',
  'group_politics-economics': '本社・政経',
  'group_sports': '本社・運動',
  'group_photo': '本社・写真',
  'group_local': '本社・地域',
  'group_culture': '本社・文化',
  'group_digital': '本社・デジタル',
  'group_editorial': '本社・論説',
  'group_publication': '本社・出版',
};

/** ラベル組み立てのオプション */
export interface UserGroupLabelOptions {
  /** true のとき Reporters グループに「記者」を付ける（メモ画面のみ） */
  includeReporter?: boolean;
}

/**
 * グループ名の配列（または単一の文字列）から表示ラベルを作る。
 *
 * 付与順は アドミン > 役員 > システム > 部署 > 支局 > デスク（> 記者）。
 * 該当が無ければ 'ユーザー' を返す。
 */
export const resolveUserGroupLabel = (
  groups: unknown,
  options: UserGroupLabelOptions = {},
): string => {
  const mappedGroupPriority = Object.keys(DEPARTMENT_GROUP_LABELS);

  const arr = Array.isArray(groups) ? groups : (typeof groups === 'string' ? [groups] : []);
  const normalized = Array.from(new Set(arr.map((g) => String(g || '').trim()).filter(Boolean)));
  const lower = normalized.map((g) => g.toLowerCase());

  const labels: string[] = [];

  const isAdmin = lower.some((g) => g === 'admins' || g === 'admin' || g.includes('admin'));
  if (isAdmin) labels.push('アドミン');

  const isReadOnly = lower.some((g) => g === 'read_only');
  if (isReadOnly) labels.push('役員');

  const isSystem = lower.some((g) => (
    g === 'systems' || g === 'system' || g.includes('system') || g === 'users' || g === 'user'
  ));
  if (isSystem) labels.push('システム');

  mappedGroupPriority.forEach((groupKey) => {
    if (normalized.includes(groupKey)) {
      labels.push(DEPARTMENT_GROUP_LABELS[groupKey]!);
    }
  });

  // branch_* は既知/未知を問わずラベル化して追加
  const branchLabel = getBranchGroupLabel(normalized);
  if (branchLabel) labels.push(branchLabel);

  const isDesk = lower.some((g) => g === 'desks' || g === 'desk' || g.includes('desk'));
  if (isDesk) labels.push('デスク');

  if (options.includeReporter) {
    const isReporter = lower.some((g) => g === 'reporters' || g === 'reporter' || g.includes('reporter'));
    if (isReporter) labels.push('記者');
  }

  return labels.length > 0 ? labels.join('・') : 'ユーザー';
};
